import {
  add,
  addDays,
  addHours,
  addSeconds,
  Duration,
  getDay,
  isAfter,
  isBefore,
  isEqual,
  max,
  min,
  nextDay,
  set,
  startOfDay,
  type Day,
} from 'date-fns';
import type { CalendarConstraintHint, CalendarConstraint } from './types';
import { isEntireSelectionAvailable } from './utils';
import type { Matcher } from 'react-day-picker';
import type { DisabledTimes } from '../../time-picker';

/**
 * min <= 선택한 시점
 */
export function constraintMin(min: Date): CalendarConstraint {
  return (_context, date) => {
    if (isBefore(date, min)) {
      return { available: false, until: min };
    }
    return { available: true, until: null };
  };
}

/**
 * 선택한 시점 < max
 */
export function constraintMax(max: Date): CalendarConstraint {
  return (_context, date) => {
    if (isAfter(date, max) || isEqual(date, max)) {
      return { available: false, until: null };
    }
    return { available: true, until: max };
  };
}

/**
 * min < 선택한 시점 < max
 */
export function constraintBetween(min: Date, max: Date): CalendarConstraint {
  return constraintAnd(constraintMin(min), constraintMax(max));
}

/**
 * 선택한 시점이 특정 요일 (0 = 일요일)에 해당하는 경우에만 허용합니다.
 */
export function constraintOnlyDaysOfTheWeek(days: number[]): CalendarConstraint {
  const minDay = Math.min(...days);
  return (_context, date) => {
    if (!days.includes(getDay(date))) {
      return { available: false, until: nextDay(date, minDay as Day) };
    }
    return { available: true, until: startOfDay(addDays(date, 1)) };
  };
}

/**
 * 선택한 시점이 평일 (월-금)인 경우에만 허용합니다.
 */
export function constraintOnlyWeekdays(): CalendarConstraint {
  return constraintOnlyDaysOfTheWeek([1, 2, 3, 4, 5]);
}

/**
 * 선택한 시점이 주말 (토-일)인 경우에만 허용합니다.
 */
export function constraintOnlyWeekends(): CalendarConstraint {
  return constraintOnlyDaysOfTheWeek([0, 6]);
}

/**
 * 시작 시점과 종료 시점 사이의 기간이 duration 미만이어야 합니다.
 */
export function constraintMaxDuration(duration: Duration): CalendarConstraint {
  return (context, date) => {
    if (context.current?.from == null) {
      return { available: true, until: null };
    }
    const maxDate = add(context.current?.from, duration);
    if (isAfter(date, maxDate) || isEqual(date, maxDate)) {
      return { available: false, until: null };
    }
    return { available: true, until: maxDate };
  };
}

/**
 * 시작 시점과 종료 시점 사이의 기간이 duration 이상이어야 합니다.
 */
export function constraintMinDuration(duration: Duration): CalendarConstraint {
  return (context, date) => {
    if (context.current?.from == null) {
      return { available: true, until: null };
    }
    const minDate = add(context.current?.from, duration);
    if (isBefore(date, minDate)) {
      return { available: false, until: minDate };
    }
    return { available: true, until: null };
  };
}

/**
 * 시작 시점과 종료 시점 사이의 기간이 seconds 미만이어야 합니다.
 */
export function constraintMaxSeconds(seconds: number): CalendarConstraint {
  return constraintMaxDuration({ seconds });
}

/**
 * 시작 시점과 종료 시점 사이의 기간이 seconds 이상이어야 합니다.
 */
export function constraintMinSeconds(seconds: number): CalendarConstraint {
  return constraintMinDuration({ seconds });
}

/**
 * 두 constraint 연산 결과 간 우선 순위를 계산합니다.
 * - -1: a < b
 * -  1: b > a
 * -  0: a = b
 */
function orderCalendarValidationHint(a: CalendarConstraintHint, b: CalendarConstraintHint): number {
  if (a.until === b.until) return 0;
  if (a.until === null) return 1;
  if (b.until === null) return -1;
  if (isAfter(a.until, b.until)) return 1;
  return -1;
}

/**
 * 여러 validator를 AND 조건으로 합칩니다. (a && b && c && ...)
 */
export function constraintAnd(...validators: CalendarConstraint[]): CalendarConstraint {
  return (context, date) => {
    if (validators.length === 0) {
      return { available: true, until: null };
    }
    const results = validators.map((validator) => validator(context, date));
    results.sort(orderCalendarValidationHint);
    const errors = results.filter((v) => !v.available);
    if (errors.length > 0) {
      return { ...errors[errors.length - 1], hideInterval: results.some((v) => v.hideInterval) };
    }
    return results[0];
  };
}

/**
 * 여러 validator를 OR 조건으로 합칩니다. (a || b || c || ...)
 */
export function constraintOr(...validators: CalendarConstraint[]): CalendarConstraint {
  return (context, date) => {
    if (validators.length === 0) {
      return { available: false, until: null };
    }
    const results = validators.map((validator) => validator(context, date));
    results.sort(orderCalendarValidationHint);
    const availableResults = results.filter((v) => v.available);
    if (availableResults.length > 0) {
      return availableResults[availableResults.length - 1];
    }
    return { ...results[0], hideInterval: results.every((v) => v.hideInterval) };
  };
}

/**
 * validator의 반대 결과를 반환합니다 (!a)
 */
export function constraintNot(validator: CalendarConstraint): CalendarConstraint {
  return (context, date) => {
    const result = validator(context, date);
    return { available: !result.available, until: result.until };
  };
}

/**
 * 날짜 구간의 모든 구간이 조건에 해당하는지 (즉, 연속적인 구간이 모두 조건을 충족하는지) 확인합니다.
 */
export function constraintEnforceContinuous(validator: CalendarConstraint): CalendarConstraint {
  return (context, date) => {
    if (context.current?.from == null) {
      return validator(context, date);
    }
    const result = validator(context, date);
    const isAvailable = isEntireSelectionAvailable(validator, context, { from: context.current.from, to: date });
    return { available: result.available && isAvailable, until: result.until };
  };
}

/**
 * items에 여러 구간이 존재할 때, 각 구간 사이에 최소 seconds만큼의 여백이 존재하도록 강제합니다.
 */
export function constraintSecondsBetweenItems(seconds: number): CalendarConstraint {
  return (context, date) => {
    const { items } = context;

    // Nearest previous item's end (max to <= date)
    let prevTo: Date | null = null;
    // Nearest next item's start (min from >= date)
    let nextFrom: Date | null = null;

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.to <= date && (prevTo == null || it.to > prevTo)) prevTo = it.to;
      if (it.from >= date && (nextFrom == null || it.from < nextFrom)) nextFrom = it.from;
    }

    const minAllowed = prevTo ? addSeconds(prevTo, seconds) : null; // lower bound (exclusive via seconds)
    const maxAllowed = nextFrom ? addSeconds(nextFrom, -seconds) : null; // upper bound

    if (minAllowed != null && isBefore(date, minAllowed)) {
      return { available: false, until: minAllowed };
    }
    if (maxAllowed != null && isAfter(date, maxAllowed)) {
      // Advancing forward won't help within this bracket
      return { available: false, until: null };
    }
    return { available: true, until: maxAllowed };
  };
}

/**
 * 선택한 시각이 해당 시각 이후인지 확인합니다. (9:00:00 지정시 각 날짜 해당 시각 이후에만 선택 가능)
 */
export function constraintTimeMin(hours: number, minutes = 0, seconds = 0): CalendarConstraint {
  return (_context, date) => {
    const min = set(date, { hours, minutes, seconds, milliseconds: 0 });
    if (isBefore(date, min)) {
      return { available: false, until: min };
    }
    return { available: true, until: startOfDay(addDays(date, 1)) };
  };
}

/**
 * 선택한 시각이 해당 시각 이전인지 확인합니다. (18:00:00 지정시 각 날짜 해당 시각 이전에만 선택 가능)
 */
export function constraintTimeMax(hours: number, minutes = 0, seconds = 0): CalendarConstraint {
  return (_context, date) => {
    const max = set(date, { hours, minutes, seconds, milliseconds: 0 });
    if (isAfter(date, max) || isEqual(date, max)) {
      return { available: false, until: startOfDay(addDays(date, 1)) };
    }
    return { available: true, until: max };
  };
}

/**
 * 모든 시점을 허용합니다.
 */
export function constraintTrue(): CalendarConstraint {
  return () => {
    return { available: true, until: null };
  };
}

/**
 * 모든 시점을 불허합니다.
 */
export function constraintFalse(): CalendarConstraint {
  return () => {
    return { available: false, until: null };
  };
}

/**
 * 각 날짜 (day)에 대해 checker 함수를 실행해서 각 날짜의 유효성을 확인합니다.
 */
export function constraintDateDiscreteChecker(checker: (date: Date) => boolean): CalendarConstraint {
  return (_context, date) => {
    const start = startOfDay(date);
    const isValid = checker(start);
    return { available: isValid, until: startOfDay(addDays(date, 1)) };
  };
}

/**
 * react-day-picker의 Matcher 패턴을 CalendarConstraint로 변환합니다.
 */
export function constraintDayPickerMatcher(matcher: Matcher | Matcher[]): CalendarConstraint {
  if (typeof matcher === 'boolean') {
    if (matcher) {
      return constraintTrue();
    } else {
      return constraintFalse();
    }
  } else if (typeof matcher === 'function') {
    return constraintDateDiscreteChecker(matcher);
  } else if (matcher instanceof Date) {
    return constraintBetween(startOfDay(matcher), addDays(startOfDay(matcher), 1));
  } else if (Array.isArray(matcher)) {
    return constraintOr(...matcher.map((item) => constraintDayPickerMatcher(item)));
  } else if ('from' in matcher) {
    if (matcher.from != null && matcher.to != null) {
      return constraintBetween(
        startOfDay(min([matcher.from, matcher.to])),
        addDays(startOfDay(max([matcher.from, matcher.to])), 1),
      );
    } else if (matcher.from != null) {
      return constraintMin(startOfDay(matcher.from));
    } else if (matcher.to != null) {
      return constraintMax(addDays(startOfDay(matcher.to), 1));
    }
  } else if ('before' in matcher && 'after' in matcher) {
    if (matcher.after < matcher.before) {
      return constraintBetween(addDays(startOfDay(matcher.after), 1), startOfDay(matcher.before));
    } else {
      return constraintOr(
        constraintMax(startOfDay(matcher.before)),
        constraintMin(addDays(startOfDay(matcher.after), 1)),
      );
    }
  } else if ('before' in matcher) {
    return constraintMax(startOfDay(matcher.before));
  } else if ('after' in matcher) {
    return constraintMin(addDays(startOfDay(matcher.after), 1));
  } else if ('dayOfWeek' in matcher) {
    return constraintOnlyDaysOfTheWeek(matcher.dayOfWeek);
  }
  return constraintTrue();
}

/**
 * 각 날짜(day)별로 1분 별로 구분된 비트맵을 활용해서 각 시점의 유효성을 검증합니다.
 * 가령, bitmap[0]은 0시 0분을 나타내며, bitmap[60]은 1시 0분을 나타냅니다.
 * bitmap은 무조건 1,440 (60 * 24)의 길이를 가지고 있습니다.
 * bitmap의 각 원소가 0이 아닌 경우 유효한 시점, 0인 경우 불가능한 시점으로 인식합니다.
 * @param getBitmap 각 날짜에 대해 output에 비트맵을 채우는 함수입니다.
 */
export function constraintTimeBitmap(
  getBitmap: (date: Date, range: 'from' | 'to', output: Uint8Array) => void,
): CalendarConstraint {
  const buffer = new Uint8Array(1440);
  let lastDate: number | null = null;
  let lastType: 'from' | 'to' | null = null;
  return (context, date) => {
    const start = startOfDay(date);
    const type = context?.focus ?? 'from';
    if (lastDate !== start.valueOf() || type !== lastType) {
      getBitmap(date, type, buffer);
      lastDate = start.valueOf();
      lastType = type;
    }
    const offset = date.getHours() * 60 + date.getMinutes();
    const isValid = buffer[offset] !== 0;
    let untilOffset = offset + 1;
    while (untilOffset < buffer.length) {
      const isOffsetValid = buffer[untilOffset] !== 0;
      if (isValid !== isOffsetValid) {
        break;
      }
      untilOffset += 1;
    }
    return {
      available: isValid,
      until: set(start, {
        hours: Math.floor(untilOffset / 60),
        minutes: untilOffset % 60,
        seconds: 0,
        milliseconds: 0,
      }),
    };
  };
}

/**
 * PDS TimePicker에서 사용하는 DisabledTimes을 CalendarConstraint로 변환합니다.
 */
export function constraintDisabledTimes(
  getDisabledTimes: (date: Date, range?: 'from' | 'to') => DisabledTimes,
): CalendarConstraint {
  return constraintTimeBitmap((date, range, output) => {
    output.fill(1);
    const times = getDisabledTimes(date, range);
    const hours = times.disabledHours?.() ?? [];
    for (const hour of hours) {
      for (let minute = 0; minute < 60; minute += 1) {
        output[hour * 60 + minute] = 0;
      }
    }
    for (let hour = 0; hour < 24; hour += 1) {
      const minutes = times.disabledMinutes?.(hour) ?? [];
      for (const minute of minutes) {
        output[hour * 60 + minute] = 0;
      }
    }
  });
}

/**
 * 시점이 n분 단위로 맞아 떨어지도록 합니다. "5"를 지정하면 0, 5, 10, 15, ... 55 분을 지정할 수 있습니다.
 * 60과 나누어 떨어지지 않는 경우에는, 각 시점별로 나머지가 넘어가지 않기 때문에, 가령 "55"를 지정하는 경우 각 시간별로 0, 55를 지정할 수 있게 됩니다.
 */
export function constraintIncrementMinute(incrementMinute: number = 1): CalendarConstraint {
  return (context, date) => {
    const minutes = date.getMinutes();
    const remainder = minutes % incrementMinute;
    if (remainder !== 0) {
      const nextMinutes = minutes + incrementMinute - remainder;
      const until =
        nextMinutes >= 60
          ? set(addHours(date, 1), { minutes: 0, seconds: 0, milliseconds: 0 })
          : set(date, { minutes: nextMinutes, seconds: 0, milliseconds: 0 });
      return {
        available: false,
        until,
        hideInterval: true,
      };
    }
    return {
      available: true,
      until: set(date, { minutes: minutes + 1, seconds: 0, milliseconds: 0 }),
    };
  };
}
